import { InfiniteData, QueryClient } from 'react-query';

import {
  CommentDto,
  CommunityFeedItemDto,
  PaginatedCommentsDto,
  PaginatedCommunityFeedItemDto,
} from './generated';

type ReplaceCommunityFeedItem = (original: CommunityFeedItemDto) => CommunityFeedItemDto;

/**
 * This function allow to edit the cache of the community feed items.
 *
 * @param queryClient You can retrieve it using `useQueryClient()`
 * @param replace This is a function that is called for each feed item. On each feed item, you decide what it becomes
 * (such as add likes, edit the content, etc).
 */
export function editCommunityFeedItemCache(
  queryClient: QueryClient,
  replace: ReplaceCommunityFeedItem
) {
  const setQueryData = (oldFeed: InfiniteData<PaginatedCommunityFeedItemDto> | undefined) => {
    if (!oldFeed) return oldFeed;

    return {
      ...oldFeed,
      pages: oldFeed.pages.map(page => ({
        ...page,
        items: page.items.map(item => replace(item)),
      })),
    };
  };

  // Le flux est scopé par bien et par onglet (['community-feed', assetId, kind]) :
  // on met à jour toutes les variantes en une fois plutôt qu'une clé exacte.
  queryClient.setQueriesData<InfiniteData<PaginatedCommunityFeedItemDto> | undefined>(
    ['community-feed'],
    setQueryData
  );
}

type ReplaceCommunityFeedItemComment = (original: CommentDto) => CommentDto;

/**
 * This function allow to edit the cache of the community feed items comments.
 *
 * @param queryClient You can retrieve it using `useQueryClient()`
 * @param replace This is a function that is called for each feed item. On each feed item, you decide what it becomes
 * (such as add likes, edit the content, etc).
 */
export function editCommunityFeedItemCommentsCache(
  queryClient: QueryClient,
  feedItemId: string,
  replace?: ReplaceCommunityFeedItemComment,
  addOnLastPosition?: CommentDto
) {
  queryClient.setQueryData<InfiniteData<PaginatedCommentsDto>>(
    ['fetch-community-feed-item-comments', feedItemId],
    // @ts-expect-error I do not understand the error
    oldComments => {
      if (!oldComments) return oldComments;

      if (addOnLastPosition) {
        const page = oldComments.pages[oldComments.pages.length - 1];
        if (page) {
          page.items.push(addOnLastPosition);

          // The end cursor is not valid anymore because we published a comment.
          page.endCursor = null;
        }
      }

      return {
        ...oldComments,
        pages: oldComments.pages.map(page => ({
          ...page,
          items: replace ? page.items.map(item => replace(item)) : page.items,
        })),
      };
    }
  );
}

type ReplaceProjectComment = (original: CommentDto) => CommentDto;

/**
 * This function allow to edit the cache of the project comments.
 *
 * @param queryClient You can retrieve it using `useQueryClient()`
 * @param projectId The project ID
 * @param replace This is a function that is called for each comment. On each comment, you decide what it becomes.
 * @param addOnLastPosition If provided, add the comment at the last position of the list.
 */
export function editProjectCommentsCache(
  queryClient: QueryClient,
  projectId: string,
  replace?: ReplaceProjectComment,
  addOnLastPosition?: CommentDto
) {
  queryClient.setQueryData<InfiniteData<PaginatedCommentsDto>>(
    ['fetch-project-comments', projectId],
    // @ts-expect-error I do not understand the error
    oldComments => {
      if (!oldComments) return oldComments;

      if (addOnLastPosition) {
        const page = oldComments.pages[oldComments.pages.length - 1];
        if (page) {
          page.items.push(addOnLastPosition);

          // The end cursor is not valid anymore because we published a comment.
          page.endCursor = null;
        }
      }

      return {
        ...oldComments,
        pages: oldComments.pages.map(page => ({
          ...page,
          items: replace ? page.items.map(item => replace(item)) : page.items,
        })),
      };
    }
  );
}
